import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from "@/components/ui/command";
import { Search, Phone, MapPin, CornerDownLeft } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { NAV, ADMIN_NAV } from "@/components/Sidebar";
import { initials } from "@/lib/format";

const IS_MAC = typeof navigator !== "undefined" && /Mac/i.test(navigator.platform || "");

function Kbd({ children }) {
  return (
    <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-[4px] text-[10px] font-semibold tabular-nums"
      style={{ background: "rgba(10,25,47,0.06)", color: "#475569", border: "1px solid rgba(10,25,47,0.10)" }}>
      {children}
    </span>
  );
}

export default function GlobalCommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [clients, setClients] = useState([]);
  const [searching, setSearching] = useState(false);
  const nav = useNavigate();
  const { user } = useAuth();
  const role = user?.role || "admin";
  const debounceRef = useRef(null);

  // ⌘K / Ctrl+K
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Reset on close
  useEffect(() => {
    if (!open) { setQuery(""); setClients([]); }
  }, [open]);

  // Ricerca clienti debounced
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < 2) { setClients([]); setSearching(false); return; }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await api.get("/clients", { params: { q, limit: 6 } });
        setClients(r.data?.items || []);
      } catch {
        setClients([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query]);

  const go = useCallback((to) => {
    setOpen(false);
    nav(to);
  }, [nav]);

  // Voci di navigazione filtrate per ruolo + query
  const navItems = useMemo(() => {
    const all = [...NAV, ...ADMIN_NAV].filter((it) => !it.roles || it.roles.includes(role));
    const q = query.trim().toLowerCase();
    const filtered = q ? all.filter((it) => it.label.toLowerCase().includes(q)) : all;
    return filtered.slice(0, q ? 6 : 8);
  }, [role, query]);

  return (
    <>
      {/* Trigger nel header */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="cmdk-trigger"
        title="Ricerca rapida"
        className="hidden md:flex items-center gap-2 pl-3 pr-2 py-2 text-[12.5px] rounded-[6px] transition-all hover:shadow-[0_2px_8px_rgba(10,25,47,0.08)]"
        style={{ background: "#F4F6FA", border: "1px solid #E2E6ED", color: "#8A94A6" }}
      >
        <Search className="w-3.5 h-3.5" strokeWidth={2.2} />
        <span className="pr-4">Cerca…</span>
        <span className="flex items-center gap-0.5">
          <Kbd>{IS_MAC ? "⌘" : "Ctrl"}</Kbd>
          <Kbd>K</Kbd>
        </span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="overflow-hidden p-0 sm:max-w-[560px] gap-0 top-[18%] translate-y-0"
          data-testid="global-command-palette"
        >
          <DialogTitle className="sr-only">Ricerca rapida</DialogTitle>
          <Command shouldFilter={false} className="rounded-none">
            <CommandInput
              placeholder="Cerca clienti, pagine, azioni…"
              value={query}
              onValueChange={setQuery}
              data-testid="cmdk-input"
            />
            <CommandList className="max-h-[400px]">
              <CommandEmpty>
                {searching ? "Ricerca in corso…" : "Nessun risultato."}
              </CommandEmpty>

              {/* Clienti (live) */}
              {clients.length > 0 && (
                <CommandGroup heading="Clienti">
                  {clients.map((c) => {
                    const name = `${c.cognome || ""} ${c.nome || ""}`.trim() || "Cliente";
                    return (
                      <CommandItem
                        key={c.id}
                        value={`client-${c.id}`}
                        onSelect={() => go(`/clienti/${c.id}`)}
                        data-testid={`cmdk-client-${c.id}`}
                        className="cursor-pointer"
                      >
                        <div className="w-7 h-7 rounded-[4px] bg-[#0A192F] text-[#D4AB6E] flex items-center justify-center font-serif text-[11px] shrink-0">
                          {initials(c.nome, c.cognome)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-[13px] font-medium text-slate-900 truncate">{name}</div>
                          <div className="flex items-center gap-2 text-[11px] text-slate-500">
                            {c.telefono && <span className="inline-flex items-center gap-1 tabular-nums"><Phone className="!w-2.5 !h-2.5" />{c.telefono}</span>}
                            {c.citta && <span className="inline-flex items-center gap-1"><MapPin className="!w-2.5 !h-2.5" />{c.citta}</span>}
                            {c.stato && <span className="uppercase text-[9px] font-bold tracking-wider text-[#8a6e3d]">{c.stato}</span>}
                          </div>
                        </div>
                        <CornerDownLeft className="!w-3 !h-3 text-slate-300" />
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              )}

              {/* Navigazione */}
              {navItems.length > 0 && (
                <CommandGroup heading="Vai a">
                  {navItems.map((it) => {
                    const Icon = it.icon;
                    return (
                      <CommandItem
                        key={it.to}
                        value={`nav-${it.to}`}
                        onSelect={() => go(it.to)}
                        data-testid={`cmdk-nav-${it.testid}`}
                        className="cursor-pointer"
                      >
                        <div className="w-7 h-7 rounded-[4px] flex items-center justify-center shrink-0" style={{ background: "rgba(183,149,91,0.10)" }}>
                          <Icon className="!w-3.5 !h-3.5 text-[#8a6e3d]" strokeWidth={2} />
                        </div>
                        <span className="text-[13px] text-slate-800">{it.label}</span>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              )}

              {/* Azioni rapide (solo query vuota) */}
              {!query.trim() && (
                <CommandGroup heading="Azioni rapide">
                  <CommandItem value="action-daily" onSelect={() => go("/clienti?view=daily")} className="cursor-pointer" data-testid="cmdk-action-daily">
                    <div className="w-7 h-7 rounded-[4px] flex items-center justify-center shrink-0" style={{ background: "rgba(10,25,47,0.06)" }}>
                      <Phone className="!w-3.5 !h-3.5 text-[#0A192F]" strokeWidth={2} />
                    </div>
                    <span className="text-[13px] text-slate-800">Richiami di oggi</span>
                  </CommandItem>
                  <CommandItem value="action-expired" onSelect={() => go("/clienti?view=expired")} className="cursor-pointer" data-testid="cmdk-action-expired">
                    <div className="w-7 h-7 rounded-[4px] flex items-center justify-center shrink-0" style={{ background: "rgba(220,38,38,0.08)" }}>
                      <Search className="!w-3.5 !h-3.5 text-rose-600" strokeWidth={2} />
                    </div>
                    <span className="text-[13px] text-slate-800">Clienti scaduti</span>
                  </CommandItem>
                </CommandGroup>
              )}
            </CommandList>

            {/* Footer hint */}
            <div className="flex items-center gap-3 px-3 py-2 border-t border-slate-100 bg-[#FAFBFC]">
              <span className="flex items-center gap-1 text-[10.5px] text-slate-400"><Kbd>↑</Kbd><Kbd>↓</Kbd> naviga</span>
              <span className="flex items-center gap-1 text-[10.5px] text-slate-400"><Kbd>↵</Kbd> apri</span>
              <span className="flex items-center gap-1 text-[10.5px] text-slate-400"><Kbd>esc</Kbd> chiudi</span>
              <span className="ml-auto eyebrow" style={{ fontSize: 9 }}>Best Agent</span>
            </div>
          </Command>
        </DialogContent>
      </Dialog>
    </>
  );
}
